/**
 * SupportComposer — lean reply/message composer for the support widget.
 * Attachment handling (clipboard paste, drag-drop, file picker) is lifted
 * from `CommentComposer.tsx` with mentions and links stripped — attachments
 * upload immediately via `useFileUpload` (fileService.uploadFile) and are
 * sent as `{fileAssetId, filename, contentType, size}` refs, per the plan's
 * "Comment pattern, no FK" attachment shape.
 */

import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { Paperclip, Send } from 'lucide-react';
import { useSupportAttachments } from './useSupportAttachments';
import { PendingAttachmentChip } from './supportShared';
import type { SupportAttachment } from '@upskill/shared';

export interface SupportComposerProps {
  onSend: (data: { body: string; attachments: SupportAttachment[] }) => void | Promise<unknown>;
  isSending?: boolean;
  placeholder?: string;
  disabledNotice?: string;
  onBodyChange?: (body: string) => void;
  initialBody?: string;
}

const Wrapper = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 40px;
  max-height: 150px;
  padding: 8px 12px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  line-height: 1.5;
  resize: none;
  overflow-y: auto;
  background: transparent;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }
`;

const PreviewArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: 0 12px 8px;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const ToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text.light};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.light};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const SendBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary.base};
  color: ${({ theme }) => theme.colors.text.white};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary.dark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DisabledBanner = styled.div`
  padding: 6px 12px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.status.info.bg};
  border-radius: ${({ theme }) => `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`};
`;

export function SupportComposer({
  onSend,
  isSending = false,
  placeholder = 'Write a message…',
  disabledNotice,
  onBodyChange,
  initialBody = '',
}: SupportComposerProps) {
  const [text, setText] = useState(initialBody);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { pending, addFiles, removeAttachment, reset, handlePasteImage, handleDropFiles, readyAttachments, hasUploading } =
    useSupportAttachments();

  const isEmpty = !text.trim() && readyAttachments.length === 0;
  const [localSending, setLocalSending] = useState(false);
  const sending = isSending || localSending;

  const autoResize = useCallback(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 150) + 'px';
    }
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    onBodyChange?.(e.target.value);
    setTimeout(autoResize, 0);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;
    addFiles(Array.from(selected));
    e.target.value = '';
  };

  const handleSend = async () => {
    if (isEmpty || sending || hasUploading) return;
    try {
      setLocalSending(true);
      await onSend({ body: text.trim(), attachments: readyAttachments });
      setText('');
      reset();
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    } catch (err) {
      console.error('Failed to send support message:', err);
    } finally {
      setLocalSending(false);
    }
  };

  if (disabledNotice) {
    return <DisabledBanner>{disabledNotice}</DisabledBanner>;
  }

  return (
    <Wrapper onDragOver={(e) => e.preventDefault()} onDrop={handleDropFiles}>
      <TextArea
        ref={textareaRef}
        value={text}
        onChange={handleTextChange}
        onPaste={handlePasteImage}
        placeholder={placeholder}
        rows={1}
      />

      {pending.length > 0 && (
        <PreviewArea>
          {pending.map((p) => (
            <PendingAttachmentChip key={p.clientId} attachment={p} onRemove={removeAttachment} />
          ))}
        </PreviewArea>
      )}

      <ToolbarRow>
        <ToolButton type="button" title="Attach file" onClick={() => fileInputRef.current?.click()}>
          <Paperclip size={14} />
        </ToolButton>
        <SendBtn type="button" onClick={handleSend} disabled={isEmpty || sending || hasUploading}>
          <Send size={12} /> {sending ? 'Sending…' : 'Send'}
        </SendBtn>
      </ToolbarRow>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept="image/*,video/*,.pdf,.docx,.xlsx,.doc,.xls"
      />
    </Wrapper>
  );
}

export default SupportComposer;
